import { useState, type FC } from 'react';

import { type ChainId } from '@human-protocol/sdk';
import { Box, Button, Paper, Stack, styled, Typography } from '@mui/material';

import ViewDistributionDialog from '@/components/RewardsDistribution/ViewDistributionDialog';
import { useExchangesContext } from '@/providers/ExchangesProvider';
import {
  type CampaignFormValues,
  type MarketMakingFormValues,
  type HoldingFormValues,
  type ThresholdFormValues,
  CampaignType,
  type CompetitiveMmFormValues,
  type ThresholdMmFormValues,
} from '@/types';
import { getNetworkName, getTokenInfo, mapTypeToLabel } from '@/utils';
import dayjs from '@/utils/dayjs';

import { type LaunchSupportedCampaignType } from '../utils';

type Props = {
  step: number;
  chainId: ChainId | null;
  formValues: CampaignFormValues | null;
};

const Row = ({ children }: { children: React.ReactNode }) => (
  <Stack
    direction="row"
    sx={{
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      py: 1,
    }}
  >
    {children}
  </Stack>
);

const RowName = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.auxiliary,
}));

const RowValue = styled(Typography)(({ theme }) => ({
  color: theme.palette.neutral['100'],
  textAlign: 'right',
}));

const getTargetInfo = (
  campaignType: LaunchSupportedCampaignType,
  formValues: CampaignFormValues
) => {
  switch (campaignType) {
    case CampaignType.MARKET_MAKING:
      return {
        label: 'Daily volume target',
        value: (formValues as MarketMakingFormValues)?.daily_volume_target,
        token: (formValues as MarketMakingFormValues)?.pair.split('/')[1],
      };
    case CampaignType.HOLDING:
      return {
        label: 'Daily balance target',
        value: (formValues as HoldingFormValues)?.daily_balance_target,
        token: (formValues as HoldingFormValues)?.symbol,
      };
    case CampaignType.THRESHOLD:
      return {
        label: 'Minimum balance target',
        value: (formValues as ThresholdFormValues)?.minimum_balance_target,
        token: (formValues as ThresholdFormValues)?.symbol,
      };
    case CampaignType.COMPETITIVE_MARKET_MAKING:
      return {
        label: 'Minimum volume required',
        value: (formValues as CompetitiveMmFormValues)?.minimum_volume_required,
        token: (formValues as CompetitiveMmFormValues)?.pair.split('/')[1],
      };
    case CampaignType.THRESHOLD_MARKET_MAKING:
      return {
        label: 'Minimum volume target',
        value: (formValues as ThresholdMmFormValues)?.minimum_volume_target,
        token: (formValues as ThresholdMmFormValues)?.pair.split('/')[1],
      };
  }
};

const getSymbolOrPairInfo = (
  campaignType: LaunchSupportedCampaignType,
  formValues: CampaignFormValues
) => {
  switch (campaignType) {
    case CampaignType.MARKET_MAKING:
    case CampaignType.COMPETITIVE_MARKET_MAKING:
    case CampaignType.THRESHOLD_MARKET_MAKING:
      return {
        label: 'Pair',
        value: (formValues as MarketMakingFormValues)?.pair,
      };
    case CampaignType.HOLDING:
    case CampaignType.THRESHOLD:
      return {
        label: 'Symbol',
        value: (formValues as HoldingFormValues)?.symbol,
      };
  }
};

const SummaryCard: FC<Props> = ({ step, chainId, formValues }) => {
  const [openRewardsDialog, setOpenRewardsDialog] = useState(false);

  const { exchangesMap } = useExchangesContext();
  const exchangeName = exchangesMap.get(
    formValues?.exchange || ''
  )?.display_name;

  const { type: campaignType, fund_amount: fundAmount } = formValues || {};

  const isLastStep = step === 5;

  const showFormValues = step > 3;

  const isCompetitiveMm =
    campaignType === CampaignType.COMPETITIVE_MARKET_MAKING &&
    formValues &&
    'rewards_distribution' in formValues;

  const symbolOrPairLabel = campaignType
    ? getSymbolOrPairInfo(campaignType, formValues as CampaignFormValues).label
    : '';

  const symbolOrPair = campaignType
    ? getSymbolOrPairInfo(campaignType, formValues as CampaignFormValues).value
    : '';

  const targetValue = campaignType
    ? getTargetInfo(campaignType, formValues as CampaignFormValues).value
    : '';

  const targetToken = campaignType
    ? getTargetInfo(campaignType, formValues as CampaignFormValues).token
    : '';

  const renderRewardsDistributionButton = () => {
    if (!isCompetitiveMm) return null;

    if (formValues?.rewards_distribution.length === 0)
      return <RowValue>-</RowValue>;

    return (
      <Button
        variant="text"
        disableRipple
        sx={{
          p: 0,
          gap: 0.5,
          fontSize: 14,
          textDecoration: 'underline',
          '&:hover': {
            bgcolor: 'transparent',
            textDecoration: 'underline',
          },
        }}
        onClick={() => setOpenRewardsDialog(true)}
      >
        <Box
          component="span"
          sx={{
            p: 0.25,
            borderRadius: '50%',
            bgcolor: 'neutral.200',
          }}
        />
        {formValues?.rewards_distribution.length} positions
      </Button>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        gridArea: 'aside',
        display: 'flex',
        flexDirection: 'column',
        minHeight: '50px',
        height: 'fit-content',
        py: 1,
        px: 2,
        borderRadius: '8px',
        border: isLastStep ? 'none' : '1px solid',
        borderColor: 'border.strong',
      }}
    >
      <Row>
        <RowName>Network</RowName>
        <RowValue>{chainId ? getNetworkName(chainId) : '-'}</RowValue>
      </Row>
      {step > 1 && (
        <Row>
          <RowName>Campaign Type</RowName>
          <RowValue>
            {campaignType ? mapTypeToLabel(campaignType) : '-'}
          </RowValue>
        </Row>
      )}
      {step > 2 && !!formValues && (
        <>
          <Row>
            <RowName>Exchange</RowName>
            <RowValue>{showFormValues ? exchangeName : '-'}</RowValue>
          </Row>
          <Row>
            <RowName>{symbolOrPairLabel}</RowName>
            <RowValue>{showFormValues ? symbolOrPair : '-'}</RowValue>
          </Row>
          <Row>
            <RowName>Fund Token</RowName>
            <RowValue>
              {showFormValues
                ? getTokenInfo(formValues?.fund_token || '')?.label
                : '-'}
            </RowValue>
          </Row>
          <Row>
            <RowName>Start Date</RowName>
            <RowValue>
              {showFormValues && formValues?.start_date
                ? dayjs(formValues?.start_date).format('Do MMM YYYY HH:mm')
                : '-'}
            </RowValue>
          </Row>
          <Row>
            <RowName>End Date</RowName>
            <RowValue>
              {showFormValues && formValues?.end_date
                ? dayjs(formValues?.end_date).format('Do MMM YYYY HH:mm')
                : '-'}
            </RowValue>
          </Row>
          {campaignType === CampaignType.THRESHOLD_MARKET_MAKING && (
            <Row>
              <RowName>Maximum participant limit</RowName>
              <RowValue>
                {showFormValues
                  ? (formValues as ThresholdMmFormValues)?.max_participants
                  : '-'}
              </RowValue>
            </Row>
          )}
          <Row>
            <RowName>
              {campaignType
                ? getTargetInfo(campaignType, formValues).label
                : ''}
            </RowName>
            <RowValue>
              {showFormValues ? `${targetValue} ${targetToken}` : '-'}
            </RowValue>
          </Row>
        </>
      )}
      {step > 3 && (
        <Row>
          <RowName>Fund Amount</RowName>
          <RowValue>
            {fundAmount || '-'}{' '}
            {fundAmount
              ? getTokenInfo(formValues?.fund_token || '')?.label
              : ''}
          </RowValue>
        </Row>
      )}
      {step > 3 && isCompetitiveMm && (
        <Row>
          <RowName>Rewards Distribution</RowName>
          {renderRewardsDistributionButton()}
          <ViewDistributionDialog
            open={openRewardsDialog}
            onClose={() => setOpenRewardsDialog(false)}
            rewardsDistribution={formValues?.rewards_distribution || []}
            fundAmount={Number(fundAmount) || 0}
            fundToken={formValues.fund_token}
          />
        </Row>
      )}
    </Paper>
  );
};

export default SummaryCard;
